<template>
    <div class="d-flex flex-row w-100">
        <SubLayout>
            <div class="leftcolumn" slot="center">
                <div class="mainimagecontainer mb-4">
                    <div v-if="chatUsers().length && showChatUsers" class="chat-carousel-wrapper">
                        <Carousel
                            :items="chatUsers()"
                            :component="getCarouselComponent()"
                            tightCols
                            dontCenter
                            @item-click="handleChatUserClick"
                        />
                    </div>
                    <img
                        v-if="companyLoaded"
                        class="mainimage"
                        :src="company.backgroundImage || getDefaultBackground()"
                    />
                </div>
                <PostingModule
                    :data="jobs"
                    class="mb-4"
                    :resolveroute="resolveRoute"
                ></PostingModule>
                <Paper v-if="company.presentation" header="Company profile">
                    <div class="p-5" v-html="company.presentation"></div>
                </Paper>
            </div>
            <div class="flex-grow-1" slot="right">
                <CompanyBadge :company="company" class="mb-4"></CompanyBadge>
                <Paper header="Links" class="mb-4">
                    <div class="d-flex flex-row w-100 justify-content-around p-4">
                        <a
                            v-if="company.url"
                            :href="company.url"
                            class="btn btn-outline-dark"
                            target="_blank"
                        >
                            Visit Website
                        </a>
                        <a
                            v-if="company.email"
                            :href="emailUri"
                            class="btn btn-outline-dark"
                            target="_blank"
                        >
                            Send email
                        </a>
                    </div>
                    <div class="d-flex flex-row w-100 justify-content-around p-4">
                        <a v-if="company.youTube" :href="company.youTube" target="_blank">
                            <img :src="youTubeLogo" class="sociallogo" alt />
                        </a>
                        <a v-if="company.twitter" :href="company.twitter" target="_blank">
                            <img :src="twitterLogo" class="sociallogo" alt />
                        </a>
                        <a v-if="company.facebook" :href="company.facebook" target="_blank">
                            <img :src="facebookLogo" class="sociallogo" alt />
                        </a>
                        <a v-if="company.vimeo" :href="company.vimeo" target="_blank">
                            <img :src="vimeoLogo" class="sociallogo" alt />
                        </a>
                        <a v-if="company.weChat" :href="company.weChat" target="_blank">
                            <img :src="weChatLogo" class="sociallogo" alt />
                        </a>
                        <a v-if="company.instagram" :href="company.instagram" target="_blank">
                            <img :src="instagramLogo" class="sociallogo" alt />
                        </a>
                        <a v-if="company.linkedIn" :href="company.linkedIn" target="_blank">
                            <img :src="linkedInLogo" class="sociallogo" alt />
                        </a>
                    </div>
                </Paper>
                <EventList :value="events" :vFairId="this.$route.params.id"></EventList>
            </div>
        </SubLayout>
    </div>
</template>

<script>
import FacebookLogo from '~/assets/img/Facebook.png';
import TwitterLogo from '~/assets/img/Twitter.png';
import YouTubeLogo from '~/assets/img/YouTube.png';
import LinkedInLogo from '~/assets/img/LinkedIn.png';
import InstagramLogo from '~/assets/img/Instagram.png';
import WeChatLogo from '~/assets/img/WeChat.png';
import VimeoLogo from '~/assets/img/Vimeo.png';

import PostingModule from '~/shared/postingmodule/PostingModule';
import EventList from './components/EventList.vue';
import CompanyBadge from './components/CompanyBadge.vue';

import { SHOW_CHAT_IN_COMPANY_PROFILE } from '~/router';
import AuthService from '~/services/AuthService';
import RestX from 'highered-utils/restx';
import ChatUserCarouselItem from '../../companydetail/components/ChatUser.CarouselItem';
import AnalyticService from '~/services/AnalyticService';
import MainLayout from '../../../components/layouts/mainlayout/MainLayout';
import SubLayout from '../../../components/layouts/mainlayout/SubLayout';

import defaultBackground from '~/assets/img/vcf/company-default-bg.png';

export default {
    name: 'Company',
    components: {
        SubLayout,
        MainLayout,
        PostingModule,
        EventList,
        CompanyBadge,
    },
    data() {
        return {
            company: {},
            companyLoaded: false,
            companyId: undefined,
            facebookLogo: FacebookLogo,
            twitterLogo: TwitterLogo,
            youTubeLogo: YouTubeLogo,
            linkedInLogo: LinkedInLogo,
            instagramLogo: InstagramLogo,
            weChatLogo: WeChatLogo,
            vimeoLogo: VimeoLogo,
            showChatUsers: SHOW_CHAT_IN_COMPANY_PROFILE,
            now: 0,
            chatAvailabilityIntervalSeconds: 5,
            chatAvailabilityInterval: null,
            hasLoggedEvent: false,
        };
    },
    computed: {
        emailUri() {
            if (!this.company.email) {
                return '';
            }
            if (this.company.email.toLowerCase().startsWith('mailto:')) {
                return this.company.email;
            } else {
                return 'mailto:'.concat(this.company.email);
            }
        },
        jobs() {
            return this.$interfaces.posting.get({
                reactive: true,
                vfair_id: this.$route.params.id,
                company_id: this.companyId,
            }).data;
        },
        events() {
            if (this.$route.params.id) {
                return this.$interfaces.vfair.id(this.$route.params.id).events.get({
                    reactive: true,
                    ownerOrganizationId: this.companyId,
                }).data;
            } else {
                return [];
            }
        },
        availableChatUsers() {
            return SHOW_CHAT_IN_COMPANY_PROFILE
                ? this.$interfaces.communication.chat.availability.id
                      .org_id(this.$route.params.companyId)
                      .get({ reactive: true })
                : [];
        },
    },
    methods: {
        getDefaultBackground() {
            return defaultBackground;
        },
        chatUsers() {
            if (!this.availableChatUsers.data) return [];
            return this.availableChatUsers.data
                .filter(cu => cu.availableUntil > this.now)
                .map(item => ({
                    ...item,
                    companyId: this.company.organizationId,
                    companyTitle: this.company.title,
                }));
        },
        chatUsersLoading() {
            return SHOW_CHAT_IN_COMPANY_PROFILE ? this.availableChatUsers.loading : false;
        },
        resolveRoute(jobAdId) {
            const url = this.$URLS.vfair
                .concat('/', this.$route.params.id)
                .concat('/jobs/', jobAdId);
            return url;
        },
        handleChatUserClick(item) {
            if (!AuthService.isLoggedIn()) {
                RestX.getInterfaces().logInModule.toggleOpen();
            }
            this.$root.$emit('chat-conversation-create-update', item.id);
        },
        chatAvailabilityTick() {
            this.now += this.chatAvailabilityIntervalSeconds;
        },
        getCarouselComponent() {
            return ChatUserCarouselItem;
        },
        logCompanyViewEvent() {
            // This triggers an Google Analytics event
            const data = this.company;
            if (data.organizationId) {
                AnalyticService.companyViewEvent(data.organizationId, data.name);
                this.hasLoggedEvent = true;
            }
        },
    },
    mounted() {
        this.companyId = this.$route.params.companyId;
        this.$interfaces.vfair
            .id(this.$route.params.id)
            .organizations.organizationId(this.companyId)
            .get()
            .then(company => {
                this.company = company.data;
                this.companyLoaded = true;
            });
        if (SHOW_CHAT_IN_COMPANY_PROFILE) {
            this.$interfaces.communication.chat.availability.id
                .org_id(this.$route.params.companyId)
                .get({ forceUpdate: true });
            this.now = Math.round(new Date().getTime() / 1000);
            this.chatAvailabilityInterval = setInterval(
                this.chatAvailabilityTick,
                this.chatAvailabilityIntervalSeconds * 1000
            );
        }
    },
    watch: {
        company(newCompany, oldCompany) {
            // Log company-pageview if it has not been done yet.
            // If you click onto a new company from the this page, we also have to check
            // if the company-ids have updated, so we can log again.
            if (newCompany && (!this.hasLoggedEvent || newCompany.id !== oldCompany.id)) {
                this.logCompanyViewEvent();
            }
        },
    },
    beforeDestroy() {
        if (SHOW_CHAT_IN_COMPANY_PROFILE) {
            clearInterval(this.chatAvailabilityInterval);
            this.chatAvailabilityInterval = null;
        }
    },
};
</script>

<style scoped>
.mainimagecontainer {
    position: relative; /* If you want text inside of it */
}

.mainimage {
    width: 100%;
    height: 100%;
    border-radius: 10px;
    object-fit: cover;
}

.sociallogo {
    width: 26px;
}
.chat-carousel-wrapper {
    position: absolute;
    bottom: 0px;
    width: 100%;
    z-index: 1;
}
</style>
